import React from "react";
import { Box, Stack, Typography, Button, Chip, Skeleton } from "@mui/material";
import HotelIcon from "@mui/icons-material/Hotel";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

import AlertPatientCard from "./AlertPatientCard";
import { ALERT_COLORS } from "../../constants/clinicalDashboard";

const TYPE_CONFIG = {
  admitted: {
    label: "Admitted",
    icon: <HotelIcon sx={{ fontSize: 16 }} />,
  },
  discharged: {
    label: "Discharged",
    icon: <SwapHorizIcon sx={{ fontSize: 16 }} />,
  },
  emergency: {
    label: "Emergency",
    icon: <LocalHospitalIcon sx={{ fontSize: 16 }} />,
  },
};

const MAX_VISIBLE = 5;

/**
 * Columna de alertas por tipo (admitted/discharged/emergency).
 * Muestra header con count + scroll vertical de cards.
 */
export default function AlertColumn({
  type,
  patients,
  count,
  loading,
  onViewAll,
  onPatientClick,
  onCreateTask,
}) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.admitted;
  const borderColor = ALERT_COLORS[type] || "#94A3B8";
  const visiblePatients = (patients || []).slice(0, MAX_VISIBLE);

  if (loading) {
    return (
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Skeleton variant="text" width="60%" height={28} />
        <Stack spacing={1} sx={{ mt: 1 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={90} />
          ))}
        </Stack>
      </Box>
    );
  }

  const isAdmitted = type === "admitted";

  return (
    <Box sx={{ flex: 1, minWidth: 200 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box sx={{ color: borderColor, display: "flex" }}>{config.icon}</Box>
        <Typography variant="subtitle2" fontWeight={700}>
          {config.label}
        </Typography>
        <Chip
          label={count || 0}
          size="small"
          sx={{
            height: 22,
            minWidth: 28,
            fontSize: "0.72rem",
            fontWeight: 700,
            bgcolor: `${borderColor}18`,
            color: borderColor,
            ...(isAdmitted && count > 0
              ? {
                  animation: "pulseAlert 2s ease-in-out infinite",
                  "@keyframes pulseAlert": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.6 },
                  },
                }
              : {}),
          }}
        />
      </Stack>

      {/* Cards */}
      <Stack
        spacing={1}
        sx={{
          maxHeight: 380,
          overflowY: "auto",
          pr: 0.5,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#E2E8F0",
            borderRadius: 2,
          },
        }}
      >
        {visiblePatients.length === 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ py: 2, textAlign: "center", fontStyle: "italic" }}
          >
            No active alerts
          </Typography>
        ) : (
          visiblePatients.map((patient) => (
            <AlertPatientCard
              key={patient.cipId}
              patient={patient}
              type={type}
              onClick={onPatientClick ? () => onPatientClick(patient) : undefined}
              onCreateTask={onCreateTask}
            />
          ))
        )}
      </Stack>

      {/* View All */}
      {(count || 0) > MAX_VISIBLE && (
        <Button
          size="small"
          onClick={onViewAll}
          sx={{
            mt: 1,
            fontSize: "0.72rem",
            textTransform: "none",
            color: borderColor,
          }}
        >
          View All ({count}) →
        </Button>
      )}
    </Box>
  );
}
